import type { FC, ReactNode } from 'react'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { LegalPage } from '@/components/legal/legal-page'
import { company } from '@/constants/company'

const Datenschutz: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Datenschutz'
                path='/datenschutz'
                description='Datenschutzerklärung von ConcreTrack: welche Daten beim Besuch der Website und bei Anfragen verarbeitet werden und welche Rechte Sie haben.'
            />

            <LegalPage
                title='Datenschutzerklärung'
                intro='Wir nehmen den Schutz Ihrer persönlichen Daten ernst. Hier erfahren Sie, welche Daten beim Besuch dieser Website und bei einer Anfrage verarbeitet werden.'
                updated='Stand: November 2026'
            >
                <h2>1. Verantwortlicher</h2>

                <p>
                    Verantwortlich für die Datenverarbeitung auf dieser Website ist:
                </p>

                <p>
                    {company.legalName}
                    <br />
                    {company.street}
                    <br />
                    {company.postalCode} {company.city}
                    <br />
                    Vertreten durch den Geschäftsführer {company.managingDirector}
                    <br />
                    Telefon: <a href={company.phoneHref}>{company.phone}</a>
                    <br />
                    E-Mail: <a href={`mailto:${company.email}`}>{company.email}</a>
                </p>

                <h2>2. Datenschutzbeauftragter</h2>

                <p>
                    Ein Datenschutzbeauftragter ist nicht bestellt, da bei uns in der Regel weniger als 20 Personen ständig mit der automatisierten Verarbeitung personenbezogener Daten beschäftigt sind (§ 38 Abs. 1 BDSG). Bei Fragen zum Datenschutz wenden Sie sich bitte an die oben genannten Kontaktdaten.
                </p>

                <h2>3. Hosting und Server-Logfiles</h2>

                <p>
                    Diese Website wird über die Plattform Modulify betrieben, die Server, Datenbank und die Auslieferung der Inhalte bereitstellt. Beim Aufruf der Website werden automatisch Informationen verarbeitet, die Ihr Browser übermittelt (Server-Logfiles). Dazu gehören insbesondere die IP-Adresse, Datum und Uhrzeit des Zugriffs, die aufgerufene Seite, die zuvor besuchte Seite sowie Angaben zu Browser und Betriebssystem.
                </p>

                <p>
                    Diese Daten sind technisch erforderlich, um die Website sicher und stabil bereitzustellen, und werden nicht mit anderen Datenquellen zusammengeführt. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt in der fehlerfreien und sicheren Bereitstellung der Website.
                </p>

                <h2>4. Reichweitenmessung ohne Cookies</h2>

                <p>
                    Um zu verstehen, wie unsere Website genutzt wird, verwenden wir die integrierte Besucherstatistik unseres Hosting-Anbieters. Sie arbeitet ohne Cookies und wertet Seitenaufrufe, Herkunftsseiten, Länder sowie Browser-, Betriebssystem- und Gerätetypen ausschließlich in zusammengefasster Form aus. Rückschlüsse auf einzelne Personen ziehen wir daraus nicht.
                </p>

                <p>
                    Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt in der bedarfsgerechten Gestaltung und Verbesserung unseres Angebots.
                </p>

                <h2>5. Kontaktformular, E-Mail und Telefon</h2>

                <p>
                    Wenn Sie uns über das Kontaktformular, per E-Mail oder telefonisch kontaktieren, verarbeiten wir die von Ihnen mitgeteilten Angaben, zum Beispiel Name, Unternehmen, E-Mail-Adresse, Telefonnummer, Angaben zum Bauvorhaben und Ihre Nachricht, um Ihre Anfrage zu bearbeiten. Anfragen über das Kontaktformular werden in der Datenbank dieser Website gespeichert und uns zusätzlich per E-Mail zugestellt.
                </p>

                <p>
                    Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, soweit Ihre Anfrage mit einem Vertrag zusammenhängt oder der Durchführung vorvertraglicher Maßnahmen dient, im Übrigen Art. 6 Abs. 1 lit. f DSGVO auf Grundlage unseres berechtigten Interesses an der Beantwortung von Anfragen. Wir löschen die Daten sechs Monate nach Abschluss der Anfrage, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
                </p>

                <h2>6. Schriftarten</h2>

                <p>
                    Die auf dieser Website verwendeten Schriftarten werden direkt von unserem Server ausgeliefert. Beim Seitenaufruf wird keine Verbindung zu Servern von Google oder anderen Schriftanbietern hergestellt.
                </p>

                <h2>7. Cookies</h2>

                <p>
                    Wir setzen auf dieser Website keine Cookies zu Analyse- oder Marketingzwecken ein und binden keine Tracking-Dienste Dritter ein.
                </p>

                <h2>8. Externe Links und Kundenplattform</h2>

                <p>
                    Unsere Website enthält Links zu externen Angeboten, etwa zu LinkedIn. Erst wenn Sie einen solchen Link anklicken, werden Daten an den jeweiligen Anbieter übertragen. Für die dortige Verarbeitung ist der jeweilige Anbieter verantwortlich.
                </p>

                <p>
                    Der Link „Anmelden“ führt zur ConcreTrack-Plattform für Kunden. Für die Verarbeitung innerhalb der Plattform gelten die dort bereitgestellten Datenschutzhinweise.
                </p>

                <h2>9. SSL- bzw. TLS-Verschlüsselung</h2>

                <p>
                    Diese Website nutzt aus Sicherheitsgründen eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie am Schloss-Symbol in der Adresszeile Ihres Browsers und daran, dass die Adresse mit „https://“ beginnt.
                </p>

                <h2>10. Ihre Rechte</h2>

                <p>
                    Sie haben im Rahmen der gesetzlichen Bestimmungen jederzeit das Recht auf:
                </p>

                <ul>
                    <li>Auskunft über Ihre gespeicherten personenbezogenen Daten (Art. 15 DSGVO)</li>
                    <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
                    <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
                    <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                    <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
                    <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
                </ul>

                <p>
                    Wenden Sie sich dazu bitte an die oben genannten Kontaktdaten.
                </p>

                <h2>11. Beschwerderecht bei der Aufsichtsbehörde</h2>

                <p>
                    Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren (Art. 77 DSGVO). Für uns zuständig ist die Landesbeauftragte für den Datenschutz und für das Recht auf Akteneinsicht Brandenburg.
                </p>

                <h2>12. Widerspruchsrecht nach Art. 21 DSGVO</h2>

                <p>
                    Soweit wir Daten auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO verarbeiten, haben Sie das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch gegen diese Verarbeitung einzulegen. Wir verarbeiten die Daten dann nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe nachweisen, die Ihre Interessen, Rechte und Freiheiten überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.
                </p>

                <p>
                    Ihren Widerspruch richten Sie bitte an <a href={`mailto:${company.email}`}>{company.email}</a>.
                </p>

                <h2>13. Aktualität dieser Datenschutzerklärung</h2>

                <p>
                    Diese Datenschutzerklärung hat den Stand November 2026. Wir passen sie an, wenn sich unsere Website oder die rechtlichen Anforderungen ändern.
                </p>
            </LegalPage>
        </Layout>
    )
}

export default Datenschutz
